/*
  The quick-add popup.

  Works on the markup from sections/magnum-quick-add.liquid: one small buying
  panel with the product name, the price, the choices the product really has,
  quantity and add to basket.

  Picking a choice never reloads anything. The variant list is printed into the
  panel, so the price, the photo and what is in stock all change on the spot.

  Colour dots come from magnum-colours.js, the same list the ink picker on
  product pages uses, unless the colour is set in Shopify as an option swatch -
  then that is used as it comes.
*/
if (!customElements.get('magnum-quick-add')) {
  customElements.define(
    'magnum-quick-add',
    class MagnumQuickAdd extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;

        this.variants = this.readVariants();
        this.priceEl = this.querySelector('[data-mqa-price]');
        this.wasEl = this.querySelector('[data-mqa-was]');
        this.pricesEl = this.querySelector('[data-mqa-prices]');
        this.dealEl = this.querySelector('[data-mqa-deal]');
        this.saveEl = this.querySelector('[data-mqa-save]');
        this.imageEl = this.querySelector('[data-mqa-image]');
        this.variantInput = this.querySelector('[data-mqa-variant-id]');
        this.addButton = this.querySelector('[data-mqa-add]');
        this.buyRow = this.querySelector('[data-mqa-buy]');
        this.notify = this.querySelector('magnum-notify');
        this.optionBlocks = Array.from(this.querySelectorAll('[data-mqa-option]'));

        this.chosen = this.startingChoices();

        this.paintDots();
        this.wireChoices();
        this.wireQuantity();
        this.refresh();
      }

      readVariants() {
        const tag = this.querySelector('[data-mqa-variants]');
        if (!tag) return [];
        try {
          return JSON.parse(tag.textContent);
        } catch (error) {
          return [];
        }
      }

      /*
        What is picked, in option order. Options with only one value are not
        shown as a picker, but they still count towards finding the variant, so
        they are read from the line that states them.
      */
      startingChoices() {
        const chosen = [];

        this.querySelectorAll('[data-mqa-fixed]').forEach((line) => {
          chosen[Number(line.dataset.position) - 1] = line.dataset.value;
        });

        this.optionBlocks.forEach((block) => {
          const selected = block.querySelector('[data-mqa-value].is-selected') || block.querySelector('[data-mqa-value]');
          chosen[Number(block.dataset.position) - 1] = selected ? selected.dataset.mqaValue : '';
        });

        return chosen;
      }

      /* ---------- Colour dots ---------- */

      paintDots() {
        const colours = window.MagnumColours || {};

        this.querySelectorAll('[data-mqa-dot]').forEach((dot) => {
          // A colour set in Shopify wins - it came straight from the shop.
          if (dot.getAttribute('style')) return;

          const name = dot.closest('.mqa__value').dataset.mqaValue;
          const hex = colours.hexFor ? colours.hexFor(name) : null;

          if (hex) {
            dot.style.background = hex;
            dot.dataset.hex = hex;
          } else if (colours.isMixed && colours.isMixed(name)) {
            dot.classList.add('mqa__dot--mixed');
          } else {
            dot.classList.add('mqa__dot--unknown');
          }
        });
      }

      /* ---------- Picking ---------- */

      wireChoices() {
        this.optionBlocks.forEach((block) => {
          block.addEventListener('click', (event) => {
            const button = event.target.closest('.mqa__value');
            if (!button) return;

            this.chosen[Number(block.dataset.position) - 1] = button.dataset.mqaValue;
            this.refresh();
          });

          // Seven or more choices come as a dropdown rather than pills.
          const select = block.querySelector('[data-mqa-select]');
          if (select) {
            select.addEventListener('change', () => {
              this.chosen[Number(block.dataset.position) - 1] = select.value;
              this.refresh();
            });
          }
        });
      }

      wireQuantity() {
        const input = this.querySelector('[data-mqa-qty]');
        if (!input) return;

        this.querySelectorAll('[data-mqa-step]').forEach((button) => {
          button.addEventListener('click', () => {
            const next = (parseInt(input.value, 10) || 1) + Number(button.dataset.mqaStep);
            input.value = Math.max(1, next);
          });
        });
      }

      /* ---------- Keeping the panel true ---------- */

      refresh() {
        const variant = this.variants.find((option) =>
          option.options.every((value, index) => value === this.chosen[index])
        );

        this.markChoices();
        this.markSoldOut();

        // Prices come from the theme's own money filter, which some currency
        // apps wrap in markup, so they go in as markup rather than as text.
        if (this.priceEl && variant) this.priceEl.innerHTML = variant.price;
        const onSale = Boolean(variant && variant.was);
        if (this.dealEl) this.dealEl.hidden = !onSale;
        if (this.pricesEl) this.pricesEl.classList.toggle('mqa__prices--sale', onSale);
        if (onSale && this.wasEl) this.wasEl.innerHTML = variant.was;
        if (onSale && this.saveEl) this.saveEl.textContent = variant.save;
        if (this.imageEl && variant && variant.image) this.imageEl.src = variant.image;
        if (this.variantInput) this.variantInput.value = variant ? variant.id : '';

        const buyable = Boolean(variant && variant.available);

        // A sold-out choice swaps quantity and add to basket for "Notify when
        // available". A mix that does not exist at all keeps the greyed button.
        const notifyable = Boolean(variant && !variant.available && this.notify);
        if (this.buyRow) this.buyRow.hidden = notifyable;
        if (this.notify) {
          this.notify.hidden = !notifyable;
          if (notifyable && this.notify.setVariant) this.notify.setVariant(variant.id);
        }
        if (this.addButton) {
          this.addButton.disabled = !buyable;
          const label = this.addButton.querySelector('span');
          if (label) {
            label.textContent = buyable
              ? (window.variantStrings && window.variantStrings.addToCart) || 'Add to basket'
              : (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
          }
        }
      }

      /* Show what is picked, in the buttons and in the heading beside them. */
      markChoices() {
        this.optionBlocks.forEach((block) => {
          const picked = this.chosen[Number(block.dataset.position) - 1];
          const heading = block.querySelector('[data-mqa-chosen]');
          if (heading) heading.textContent = picked || '';

          block.querySelectorAll('.mqa__value').forEach((value) => {
            const on = value.dataset.mqaValue === picked;
            value.classList.toggle('is-selected', on);
            value.setAttribute('aria-pressed', on ? 'true' : 'false');
          });

          const select = block.querySelector('[data-mqa-select]');
          if (select && picked) select.value = picked;
        });
      }

      /*
        Grey out the choices that cannot be bought alongside what is already
        picked - an out of stock size, a colour that only comes in the other
        size. They still take a click, so the shopper can see for themselves
        that it is sold out rather than wondering why nothing happens.
      */
      markSoldOut() {
        this.optionBlocks.forEach((block) => {
          const position = Number(block.dataset.position) - 1;

          block.querySelectorAll('[data-mqa-value]').forEach((value) => {
            const trial = this.chosen.slice();
            trial[position] = value.dataset.mqaValue;

            const canBuy = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options.every((option, index) => index === position || option === trial[index]) &&
                variant.options[position] === trial[position]
            );

            value.classList.toggle('is-unavailable', !canBuy);

            // A dropdown line cannot be greyed out, so it says so in words.
            if (value.tagName === 'OPTION') {
              value.textContent = canBuy ? value.dataset.mqaValue : `${value.dataset.mqaValue} - Sold out`;
            }
          });
        });
      }
    }
  );
}
